import { flow, Instance, types } from 'mobx-state-tree'
import { exportFile } from '../../network/exportFile'
import { exportVaultReport } from '../../network/exportVaultReport'
import { extendAppSessionAPI } from '../../network/extendAppSession'
import { extendUserSessionAPI } from '../../network/extendUserSession'
import { getTokenizationHistory } from '../../network/getTokenizationHistory'
import { getTokenTransactionHistory } from '../../network/getTokenTransactionHistory'
import { getTokenTxnHistoryColumn } from '../../network/getTokenTxnHistoryColumn'
import { getVaultColumn } from '../../network/getVaultColumn'
import { getVaultSummary } from '../../network/getVaultSummary'
import {
  dateFormatter,
  DateFormatTypes,
  downloadFile,
  ENABLE_CSV,
  fromDateTime,
  getHHMMSS,
  getSelectedDate,
  toDateTime,
} from '../../util/util'
import { transactionFilterTableParserNew } from '../parser/view/TableParser'
import { getTransactionV3RequestBody } from '../request/requestbody'
import {
  exportType,
  mimeTypes,
  paginationSettingsWithDefaultPage1,
  VAULT_TOKEN_TNX_HISTORY_SETTING,
} from '../static/data'
import { customError } from '../static/strings'
import { RootStore } from '../store'
import { ResponseType, TransactionTypes, TransasctionApiType } from './Transaction'
import { transactionMap } from './Transactions'

const Summary = types.model('Summary', {
  id: types.optional(types.string, ''),
  value: types.optional(types.string, ''),
  key: types.optional(types.string, ''),
  info: types.optional(types.string, ''),
})

const FieldSetting = types.model('FieldSetting', {
  id: types.string,
  isChecked: types.boolean,
  title: types.string,
  rank: types.number,
  isDisable: types.optional(types.boolean, false),
})

const Position = types.model('position', {
  left: types.number,
  top: types.number,
  visible: types.boolean,
})

const DateRange = types.model('date', {
  fromDate: types.Date,
  toDate: types.Date,
  position: Position,
  dateLabel: types.optional(types.string, ''),
})

const Pagination = types.model('Pagination', {
  size: types.optional(types.number, 0),
  totalElements: types.optional(types.number, 0),
  totalPages: types.optional(types.number, 0),
  number: types.optional(types.number, 0),
})

const TokenizationRequests = types
  .model('Vault', {
    id: types.optional(types.string, ''),
    summary: types.array(Summary),
    history: types.array(transactionMap),
    selectedDate: DateRange,
    summarySelectedDate: DateRange,
    pagination: Pagination,
    fieldSetting: types.array(FieldSetting),
    tokenTxnHistory: types.array(transactionMap),
    txnsSelectedDate: DateRange,
  })
  .actions((self) => ({
    getSummaryApi: flow(function* getSummaryApi(mid) {
      let response: any
      try {
        const filteredFromDate = self?.summarySelectedDate?.fromDate ?? new Date(Date.now())
        const formattedFilteredFromData = `${dateFormatter(filteredFromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          filteredFromDate,
        )}`
        const filteredEndDate = self?.summarySelectedDate?.toDate ?? new Date(Date.now())
        const formattedFilteredToData = `${dateFormatter(filteredEndDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          filteredEndDate,
        )}`
        const req = {
          inputFilter: {
            merchantId: mid,
            startDateTime: formattedFilteredFromData,
            endDateTime: formattedFilteredToData,
            status: ['ALL'],
          },
        }
        response = yield getVaultSummary(RootStore.user.session.getTokens, req)
        const retData = self.summary.map((item, index) => {
          return {
            ...item,
            value: `${response[index]}`,
          }
        })
        self.summary = JSON.parse(JSON.stringify(retData))
      } catch (e) {
        self.summary = JSON.parse(JSON.stringify(defaultSummaryData))
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    updatePagination(selectedPage: number): void {
      self.pagination.number = selectedPage
    },
    resetPagination(): void {
      self.pagination = JSON.parse(JSON.stringify(paginationSettingsWithDefaultPage1))
    },
    fetchVaultColumns: flow(function* fetchVaultColumns() {
      if (self.fieldSetting.length > 0) {
        return null
      }
      let response
      try {
        response = yield getVaultColumn(RootStore.user.session.getTokens, RootStore.user.session.userAccessToken)
        const parsedColumn = response
        self.fieldSetting = JSON.parse(JSON.stringify([...parsedColumn]))
      } catch (e) {
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    fetchTokenTxnColumns: flow(function* fetchTokenTxnColumns() {
      if (RootStore.transactions.fieldSetting.length > 0) {
        return null
      }
      let response
      try {
        response = yield getTokenTxnHistoryColumn(
          RootStore.user.session.getTokens,
          RootStore.user.session.userAccessToken,
        )
        const parsedColumn = response
        RootStore.transactions.updateFieldSettings([...parsedColumn])
      } catch (e) {
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    fetchTokenizationHistory: flow(function* fetchTokenizationHistory(searchString?: string) {
      if (self.fieldSetting.length === 0) {
        return null
      }
      let response
      try {
        const fields = RootStore.tokenizationRequests.getCheckedColumn()
        const pageNumber = self?.pagination.number === 0 ? 1 : self?.pagination.number
        const size = self?.pagination.size < 10 ? 10 : self?.pagination.size
        const filteredFromDate = self?.selectedDate?.fromDate ?? new Date(Date.now())
        const formattedFilteredFromData = `${dateFormatter(filteredFromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          filteredFromDate,
        )}`
        const filteredEndDate = self?.selectedDate?.toDate ?? new Date(Date.now())
        const formattedFilteredToData = `${dateFormatter(filteredEndDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          filteredEndDate,
        )}`
        const req = {
          inputFilter: {
            responseType: 'CUSTOM',
            merchantId: RootStore.user.selectedMid,
            customerId: searchString,
            startDateTime: formattedFilteredFromData,
            endDateTime: formattedFilteredToData,
          },
          configParams: {
            page: pageNumber,
            recordCount: size,
            mediaType: 'JSON',
            sortType: 'DESC',
            sortColumn: 'createdAt',
          },
          outputDomains: fields,
        }

        response = yield getTokenizationHistory(RootStore.user.session.getTokens, req)
        self.history = JSON.parse(JSON.stringify(response.history))
        self.pagination = JSON.parse(JSON.stringify(response.page))
      } catch (e) {
        self.history = JSON.parse(JSON.stringify([]))
        self.pagination = paginationSettingsWithDefaultPage1
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    fetchTokenTxnHistory: flow(function* fetchTokenTxnHistory() {
      if (RootStore.transactions.fieldSetting.length === 0) {
        return null
      }
      let response
      try {
        const fields = RootStore.tokenizationRequests.getTxnsCheckedColumn()
        const pageNumber = self?.pagination.number === 0 ? 1 : self?.pagination.number
        const size = self?.pagination.size < 10 ? 10 : self?.pagination.size
        const filteredFromDate = self?.txnsSelectedDate?.fromDate ?? new Date(Date.now())
        const formattedFilteredFromData = `${dateFormatter(filteredFromDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          filteredFromDate,
        )}`
        const filteredEndDate = self?.txnsSelectedDate?.toDate ?? new Date(Date.now())
        const formattedFilteredToData = `${dateFormatter(filteredEndDate, DateFormatTypes.YYYYMMDD)}${getHHMMSS(
          filteredEndDate,
        )}`
        const reqBody = getTransactionV3RequestBody({
          merchantId: RootStore.user.selectedMid,
          startDateTime: formattedFilteredFromData,
          endDateTime: formattedFilteredToData,
          transactionType: TransasctionApiType.transaction,
          responseType: 'CUSTOM',
          outputDomains: fields,
          paymentMethods: RootStore.transactions.filter.paymentMode,
          transactionStatus: RootStore.transactions.filter.transactionStatus,
          page: pageNumber,
          recordCount: size,
          sortType: 'DESC',
          minimumTransactionAmount: RootStore.transactions.filter.min,
          maximumTransactionAmount: RootStore.transactions.filter.max,
          mediaType: 'JSON',
          fetchTokenizedTransactions: 'Y',
        })

        response = yield getTokenTransactionHistory(RootStore.user.session.getTokens, reqBody)
        self.tokenTxnHistory = JSON.parse(JSON.stringify(response.history))
        self.pagination = JSON.parse(JSON.stringify(response.page))
      } catch (e) {
        self.tokenTxnHistory = JSON.parse(JSON.stringify([]))
        self.pagination = paginationSettingsWithDefaultPage1
        RootStore?.response?.setMessage(e?.data?.error?.message ?? customError.errorOccured)
        throw new Error(e?.data?.error?.message ?? 'Error while processing request')
      }
      return response
    }),
    exportVaultReports: flow(function* exportVaultReports(type) {
      const fields = RootStore.tokenizationRequests.getCheckedColumn()
      const toDate = toDateTime(self?.selectedDate?.toDate ?? new Date(Date.now())) ?? new Date(Date.now())
      const fromDate = fromDateTime(self?.selectedDate?.fromDate)
      const enDate = +toDate > Date.now() ? Date.now() - 300000 : toDate // 5 min before txn

      const req = {
        inputFilter: {
          merchantId: RootStore.user.selectedMid,
          responseType: 'CUSTOM',
          startDateTime: fromDate, //dateFormatter(fromDate, DateFormatTypes.DATE_LOCAL),
          endDateTime: `${enDate}`,
        },
        configParams: {
          mediaType: exportType.get(type),
          sortColumn: 'createdAt',
          sortType: 'DESC',
        },
        outputDomains: fields,
      }
      let resp
      try {
        resp = yield exportVaultReport(RootStore.user.session.getTokens, req)
        // Checking if arraybuffer can be converted to string if yes its json and if not it's a file and it will downloaded
        try {
          //check if arraybuffer is smaller than 78 then convert it to string
          //if conversion does not give json object we can assume its file
          //if it is more than 78 then we can assume its file without conversion
          const converArrBufferToString = resp?.data instanceof ArrayBuffer ? undefined : resp?.data
          if (converArrBufferToString) {
            const mediaType = converArrBufferToString?.data?.mediaType?.replace('.', '')
            if (converArrBufferToString?.data?.status === 'INPROCESS') {
              if (ENABLE_CSV && mediaType !== type) {
                RootStore.reports.setMessage(
                  `Your report will be available in the 'Downloads' section in some time in ${mediaType} format .`,
                )
              } else {
                RootStore.reports.setMessage("Your report will be available in the 'Downloads' section in some time.")
              }
            } else if (converArrBufferToString?.data?.status?.toLowerCase() === 'failed') {
              RootStore?.response?.setMessage('Export file failed')
            }
            return converArrBufferToString
          }
        } catch (e) {
          console.log('conversion failed as response is a file arraybuffer it will download from below if block')
        }
        if (window && resp && resp?.data) {
          let contentType = resp.headers['content-type']
          //check for undefined file download on session expire
          if (mimeTypes.get(contentType) === undefined) {
            return
          }
          let fileInfo = resp.headers['content-disposition']
          const fileName = fileInfo?.slice(fileInfo.indexOf('=') + 1, fileInfo.lastIndexOf('.')) ?? 'file'
          const blob = new Blob([resp?.data], {
            type: contentType,
          })
          const url = window.URL.createObjectURL(blob)
          const a = document.createElement('a')
          document.body.appendChild(a)
          a.href = url
          a.download = fileName + `${mimeTypes.get(contentType)}`
          a.click()
          window.URL.revokeObjectURL(url)
          document.body.removeChild(a)
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.message ?? customError.errorOccured)
      }
      return resp
    }),
    exportFilesTxnV3: flow(function* exportFilesTxnV3(type, path, selectedDateInfo) {
      const fields = RootStore.transactions.getCheckedColumn()
      let txntype = TransasctionApiType.transaction

      const mid = RootStore.user.selectedMid
      const toDate =
        toDateTime(selectedDateInfo?.toDate ?? new Date(Date.now())) ??
        toDateTime(RootStore.settings.metaData.selectedDate.toDate ?? new Date(Date.now()))
      const fromDate =
        fromDateTime(selectedDateInfo?.fromDate) ?? fromDateTime(RootStore.settings.metaData.selectedDate.fromDate)
      // const toDate = toDateTime(RootStore.settings.metaData.selectedDate.toDate ?? new Date(Date.now()))
      // const fromDate = fromDateTime(RootStore.settings.metaData.selectedDate.fromDate)
      const enDate = +toDate > Date.now() ? Date.now() - 300000 : toDate // 5 min before txn
      const pMethod = RootStore.transactions.filter.paymentMode.filter((item: any) => item !== 'ALL')
      const tStatus = RootStore.transactions.filter.transactionStatus
        .filter((item: any) => item !== 'ALL')
        .map((item: any) => item.toUpperCase())

      const min = +RootStore.transactions.filter.min === 0 ? null : +RootStore.transactions.filter.min
      const max = +RootStore.transactions.filter.max === 0 ? null : +RootStore.transactions.filter.max

      const req = {
        inputFilter: {
          merchantId: mid,
          responseType: ResponseType.CUSTOM,
          startDateTime: fromDate, //dateFormatter(fromDate, DateFormatTypes.DATE_LOCAL),
          endDateTime: `${enDate}`,
          transactionType: txntype,
          paymentMethods: pMethod,
          status: tStatus,
          minimumTransactionAmount: min,
          maximumTransactionAmount: max,
          fetchTokenizedTransactions: 'Y',
        },
        configParams: {
          mediaType: exportType.get(type),
          sortColumn: 'transactionDateTime',
          sortType: 'DESC',
        },
        outputDomains: fields,
      }
      console.log('exportFilesTxnV3', { req })
      let resp
      try {
        resp = yield exportFile(RootStore.user.session.getTokens, req, true)
        const contentType = resp.headers.get('content-type')
        if (contentType === 'application/json' || !contentType) {
          resp = yield resp.json()
          if (resp?.status === 'SUCCESS' && resp?.data?.status === 'INPROCESS') {
            const mediaType = resp?.data?.mediaType?.replace('.', '')
            if (ENABLE_CSV && mediaType !== type) {
              RootStore.reports.setMessage(
                `Your report will be available in the 'Downloads' section in some time in ${mediaType} format .`,
              )
            } else {
              RootStore.reports.setMessage("Your report will be available in the 'Downloads' section in some time.")
            }
          } else if (resp?.status === 'SUCCESS' && resp?.data?.status === 'FAILED') {
            RootStore?.response?.setMessage('Export file failed')
          } else if (resp.status === 'FAILURE' && resp.error.code === '33306') {
            yield extendAppSessionAPI()
            yield extendUserSessionAPI()
            return (self as any).exportFilesTxnV3(type, path, selectedDateInfo)
          } else if (resp.status === 'FAILURE' && resp.error.code === '33607') {
            yield extendUserSessionAPI()
            return (self as any).exportFilesTxnV3(type, path, selectedDateInfo)
          } else {
            RootStore?.response?.setMessage(resp.error?.message ?? customError.errorOccured)
          }
        } else {
          downloadFile(resp, 'file')
        }
      } catch (e) {
        RootStore?.response?.setMessage(e?.message ?? customError.errorOccured)
      }
      return resp
    }),
    updateDates(dateObj: any): void {
      const own = self
      own.selectedDate.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.selectedDate.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.selectedDate.position.top = dateObj?.position?.top ?? 0
      own.selectedDate.position.left = dateObj?.position?.left ?? 0
      own.selectedDate.position.visible = dateObj?.position?.visible ?? false
      own.selectedDate.dateLabel = dateObj?.dateLabel
    },
    updateSummaryDates(dateObj: any): void {
      const own = self
      own.summarySelectedDate.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.summarySelectedDate.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.summarySelectedDate.position.top = dateObj?.position?.top ?? 0
      own.summarySelectedDate.position.left = dateObj?.position?.left ?? 0
      own.summarySelectedDate.position.visible = dateObj?.position?.visible ?? false
      own.summarySelectedDate.dateLabel = dateObj?.dateLabel
    },
    updateTxnDates(dateObj: any): void {
      const own = self
      own.txnsSelectedDate.fromDate = dateObj?.fromDate ?? new Date(Date.now())
      own.txnsSelectedDate.toDate = dateObj?.toDate ?? new Date(Date.now())
      own.txnsSelectedDate.position.top = dateObj?.position?.top ?? 0
      own.txnsSelectedDate.position.left = dateObj?.position?.left ?? 0
      own.txnsSelectedDate.position.visible = dateObj?.position?.visible ?? false
      own.txnsSelectedDate.dateLabel = dateObj?.dateLabel
    },
  }))
  .views((self) => ({
    getSummary() {
      return self?.summary?.map((item: any, index: number) => {
        return {
          id: index,
          title: item?.value ?? 'NA',
          subtitle: item?.key,
          tooltip: item.info,
        }
      })
    },
    getVaultHistory() {
      const records = transactionFilterTableParserNew(self.history, self.fieldSetting)

      const size = records.length
      if (size === 1) {
        return []
      }
      return [...records.slice(0)]
    },
    getTokenTxnHistory() {
      console.log('records', JSON.stringify(RootStore.transactions.fieldSetting))
      const trxn = transactionFilterTableParserNew(
        self.tokenTxnHistory.filter((obj) => obj.get('txnType') === TransactionTypes.Transaction),
        RootStore.transactions.fieldSetting
          .filter((field) => VAULT_TOKEN_TNX_HISTORY_SETTING.includes(field.id))
          .map((item) => ({ ...item, isChecked: true, isDisable: true })),
      )
      const size = trxn.length
      if (size === 1) {
        return []
      }
      return [...trxn.slice(0)]
    },
    getCheckedColumn(): any[] {
      let Columns: any[] = []
      self.fieldSetting.forEach((item) => {
        if (item.isChecked === true) {
          Columns.push(item)
        }
      })
      const columnsdata = Columns.reduce((obj, item) => Object.assign(obj, { [item.id]: item.isChecked }), {})
      return Object.keys(columnsdata)
    },
    getCheckedColumnTitles(): any[] {
      let columns: any[] = self.fieldSetting.filter((item) => item.isChecked)
      const sortedColumns = columns.sort((a, b) => a.rank - b.rank)
      const columnsdata = sortedColumns.reduce((obj, item) => Object.assign(obj, { [item.id]: item.title }), {})
      return columnsdata
    },
    getTxnsCheckedColumn(): any[] {
      let Columns: any[] = []
      RootStore.transactions.fieldSetting.forEach((item) => {
        if (item.isChecked === true) {
          Columns.push(item)
        }
      })
      const columnsdata = Columns.reduce((obj, item) => Object.assign(obj, { [item.id]: item.isChecked }), {})
      return Object.keys(columnsdata)
    },
    getTxnsCheckedColumnTitles(): any[] {
      let columns: any[] = RootStore.transactions.fieldSetting.filter((item) => item.isChecked)
      const sortedColumns = columns.sort((a, b) => a.rank - b.rank)
      const columnsdata = sortedColumns.reduce((obj, item) => Object.assign(obj, { [item.id]: item.title }), {})
      return columnsdata
    },
  }))

const defaultSummaryData = [
  {
    id: '1',
    value: 'NA',
    key: 'Request Received',
    info: 'Count of Tokenisation Request Received',
  },
  {
    id: '2',
    value: 'NA',
    key: 'Tokenized Count',
    info: 'Count of Successful Tokenisation',
  },
  {
    id: '3',
    value: 'NA',
    key: 'Tokenised TXN',
    info: 'Total Count of transactions done through tokenised cards',
  },
  {
    id: '4',
    value: 'NA',
    key: 'Amount of tokenised TXN',
    info: 'Total transaction amount done through tokenised cards',
  },
]
export const TokenizationRequestsData = TokenizationRequests.create({
  id: '1',
  summary: defaultSummaryData,
  history: [],
  selectedDate: {
    fromDate: getSelectedDate(1),
    toDate: new Date(Date.now()),
    dateLabel: 'Today',
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
  },
  txnsSelectedDate: {
    fromDate: getSelectedDate(1),
    toDate: new Date(Date.now()),
    dateLabel: 'Today',
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
  },
  summarySelectedDate: {
    fromDate: getSelectedDate(1),
    toDate: new Date(Date.now()),
    dateLabel: 'Today',
    position: {
      top: 0,
      left: 0,
      visible: false,
    },
  },
  fieldSetting: [],
  pagination: paginationSettingsWithDefaultPage1,
})
export type TokenizationRequestsType = Instance<typeof TokenizationRequests>
export { TokenizationRequests }
